import {
  InternalRefetchQueriesInclude,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import {useReviewContext} from '../../contexts/ReviewContext';
import {
  cancelReportPack,
  deleteReportPack,
  deleteReportPackForm,
  reportPackQuery,
  submitReportPack,
  upsertIsaApplicationForm,
  upsertIsaTransferForm,
  upsertJisaApplicationForm,
  upsertJISATransferForm,
  upsertMultreesDdmForm,
  upsertNomineeTransferForm,
  upsertReportPack,
  upsertSippContributionForm,
  upsertSippDdmForm,
  upsertSippTransferForm,
  getGenericDocumentQuery,
  uploadDocumentToReportPack,
  uploadGenericDocumentMutation,
  uploadExpressionOfWishesFormMutation,
  uploadFurtherActionRequiredFormMutation,
  uploadBankDetailCollectionFormMutation,
  amendReportPack,
  resendReportPackEmail,
  markReportPackAsNoLongerValidMutation,
  DISABLE_GENERIC_DOCUMENT_MUTATION,
  GET_GENERIC_DOCUMENT_URL_QUERY,
  uploadSmartflowToReportPack,
  deleteReportPackSmartflowExecution,
} from './reportPackQueries';
import {isValidReviewId} from './ReportPackHelpers';

import {
  UpsertIsaTransferForm,
  UpsertIsaTransferFormVariables,
} from '../../schema-types/UpsertIsaTransferForm';
import {
  UpsertJisaTransferForm,
  UpsertJisaTransferFormVariables,
} from '../../schema-types/UpsertJisaTransferForm';
import {
  UpsertIsaApplicationForm,
  UpsertIsaApplicationFormVariables,
} from '../../schema-types/UpsertIsaApplicationForm';
import {
  UpsertNomineeTransferForm,
  UpsertNomineeTransferFormVariables,
} from '../../schema-types/UpsertNomineeTransferForm';
import {
  UpsertJisaApplicationForm,
  UpsertJisaApplicationFormVariables,
} from '../../schema-types/UpsertJisaApplicationForm';
import {
  UpsertSIPPContributionForm,
  UpsertSIPPContributionFormVariables,
} from '../../schema-types/UpsertSIPPContributionForm';
import {
  DeleteReportPackForm,
  DeleteReportPackFormVariables,
} from '../../schema-types/DeleteReportPackForm';
import {
  UpsertReportPack,
  UpsertReportPackVariables,
} from '../../schema-types/UpsertReportPack';
import {
  SubmitReportPack,
  SubmitReportPackVariables,
} from '../../schema-types/SubmitReportPack';
import {
  CancelReportPack,
  CancelReportPackVariables,
} from '../../schema-types/CancelReportPack';
import {
  DeleteReportPack,
  DeleteReportPackVariables,
} from '../../schema-types/DeleteReportPack';
import {
  UpsertMultreesDdmForm,
  UpsertMultreesDdmFormVariables,
} from '../../schema-types/UpsertMultreesDdmForm';
import {
  UpsertSippTransferForm,
  UpsertSippTransferFormVariables,
} from '../../schema-types/UpsertSippTransferForm';
import {
  UpsertSippDdmForm,
  UpsertSippDdmFormVariables,
} from '../../schema-types/UpsertSippDdmForm';
import {GetGenericDocuments} from '../../schema-types/GetGenericDocuments';
import {
  UploadDocumentToReportPack,
  UploadDocumentToReportPackVariables,
} from '../../schema-types/UploadDocumentToReportPack';
import {
  UploadGenericDocument,
  UploadGenericDocumentVariables,
} from '../../schema-types/UploadGenericDocument';
import {
  UpsertExpressionOfWishesForm,
  UpsertExpressionOfWishesFormVariables,
} from '../../schema-types/UpsertExpressionOfWishesForm';
import {
  UpsertFurtherActionRequiredForm,
  UpsertFurtherActionRequiredFormVariables,
} from '../../schema-types/UpsertFurtherActionRequiredForm';
import {
  UpsertBankDetailCollectionForm,
  UpsertBankDetailCollectionFormVariables,
} from '../../schema-types/UpsertBankDetailCollectionForm';
import {
  AmendReportPack,
  AmendReportPackVariables,
} from '../../schema-types/AmendReportPack';
import {
  ResendReportPackEmail,
  ResendReportPackEmailVariables,
} from '../../schema-types/ResendReportPackEmail';
import {
  ReportPackQuery,
  ReportPackQueryVariables,
} from '../../schema-types/ReportPackQuery';
import {
  MarkReportPackAsNoLongerValid,
  MarkReportPackAsNoLongerValidVariables,
} from '../../schema-types/MarkReportPackAsNoLongerValid';
import {
  UploadSmartflowToReportPack,
  UploadSmartflowToReportPackVariables,
} from '../../schema-types/UploadSmartflowToReportPack';
import {
  DeleteReportPackSmartflowExecution,
  DeleteReportPackSmartflowExecutionVariables,
} from '../../schema-types/DeleteReportPackSmartflowExecution';

export function useReportPack() {
  const {reviewId} = useReviewContext();
  const {
    data: getGenericDocumentQueryData,
    loading: loadingGenericDocuments,
    refetch: refetchGenericDocuments,
  } = useQuery<GetGenericDocuments>(getGenericDocumentQuery, {
    fetchPolicy: 'network-only',
  });

  // Only refetch the report-pack query when we have a valid reviewId — it
  // requires `$reviewId: UUID!`, so refetching with a null/undefined id throws
  // "Variable '$reviewId' of non-null type 'UUID!' must not be null" (ON-8460).
  const refetchQueries: InternalRefetchQueriesInclude = isValidReviewId(reviewId)
    ? [{query: reportPackQuery, variables: {reviewId}}]
    : [];
  const [onUpsertISATransferForm] = useMutation<
    UpsertIsaTransferForm,
    UpsertIsaTransferFormVariables
  >(upsertIsaTransferForm, {refetchQueries});
  const [onUpsertJISATransferForm] = useMutation<
    UpsertJisaTransferForm,
    UpsertJisaTransferFormVariables
  >(upsertJISATransferForm, {refetchQueries});
  const [onUpsertIsaApplicationForm] = useMutation<
    UpsertIsaApplicationForm,
    UpsertIsaApplicationFormVariables
  >(upsertIsaApplicationForm, {refetchQueries});
  const [onUpsertNomineeTransferForm] = useMutation<
    UpsertNomineeTransferForm,
    UpsertNomineeTransferFormVariables
  >(upsertNomineeTransferForm, {refetchQueries});
  const [onUpsertJisaApplicationForm] = useMutation<
    UpsertJisaApplicationForm,
    UpsertJisaApplicationFormVariables
  >(upsertJisaApplicationForm, {refetchQueries});
  const [onUpsertSippContributionForm] = useMutation<
    UpsertSIPPContributionForm,
    UpsertSIPPContributionFormVariables
  >(upsertSippContributionForm, {refetchQueries});
  const [onUpsertMultreesDdmForm] = useMutation<
    UpsertMultreesDdmForm,
    UpsertMultreesDdmFormVariables
  >(upsertMultreesDdmForm, {refetchQueries});
  const [onUpsertSippTransferForm] = useMutation<
    UpsertSippTransferForm,
    UpsertSippTransferFormVariables
  >(upsertSippTransferForm, {refetchQueries});
  const [onUpsertSippDdmForm] = useMutation<
    UpsertSippDdmForm,
    UpsertSippDdmFormVariables
  >(upsertSippDdmForm, {refetchQueries});
  const [onUpsertExpressionOfWishesForm] = useMutation<
    UpsertExpressionOfWishesForm,
    UpsertExpressionOfWishesFormVariables
  >(uploadExpressionOfWishesFormMutation, {refetchQueries});
  const [onUpsertFurtherActionRequiredForm] = useMutation<
    UpsertFurtherActionRequiredForm,
    UpsertFurtherActionRequiredFormVariables
  >(uploadFurtherActionRequiredFormMutation, {refetchQueries});
  const [onUpsertBankDetailCollectionForm] = useMutation<
    UpsertBankDetailCollectionForm,
    UpsertBankDetailCollectionFormVariables
  >(uploadBankDetailCollectionFormMutation, {refetchQueries});
  const [onDeleteForm] = useMutation<DeleteReportPackForm, DeleteReportPackFormVariables>(
    deleteReportPackForm,
    {refetchQueries}
  );
  const [
    onUploadSmartflowToReportPack,
    {
      loading: loadingUploadSmartflow,
      data: uploadSmartflowData,
      error: uploadSmartflowError,
    },
  ] = useMutation<UploadSmartflowToReportPack, UploadSmartflowToReportPackVariables>(
    uploadSmartflowToReportPack,
    {refetchQueries}
  );
  const [
    onDeleteReportPackSmartflowExecution,
    {loading: loadingDeleteSmartflowExecution},
  ] = useMutation<
    DeleteReportPackSmartflowExecution,
    DeleteReportPackSmartflowExecutionVariables
  >(deleteReportPackSmartflowExecution, {refetchQueries});
  const [onUpsert, {loading: upsertLoading, data: upsertData, error: upsertError}] =
    useMutation<UpsertReportPack, UpsertReportPackVariables>(upsertReportPack, {
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [onSubmit, {loading: submitLoading}] = useMutation<
    SubmitReportPack,
    SubmitReportPackVariables
  >(submitReportPack, {refetchQueries});
  const [onCancelReportPack, {loading: cancelLoading}] = useMutation<
    CancelReportPack,
    CancelReportPackVariables
  >(cancelReportPack, {refetchQueries, awaitRefetchQueries: true});
  const [onDeleteReportPack, {loading: deleteLoading}] = useMutation<
    DeleteReportPack,
    DeleteReportPackVariables
  >(deleteReportPack, {refetchQueries, awaitRefetchQueries: true});
  const [onAmendReportPack, {loading: amendLoading}] = useMutation<
    AmendReportPack,
    AmendReportPackVariables
  >(amendReportPack, {refetchQueries});

  const [onResendReportPackEmail, {loading: resendEmailLoading, data: resendEmailData}] =
    useMutation<ResendReportPackEmail, ResendReportPackEmailVariables>(
      resendReportPackEmail
    );

  const [onUploadGenericDocument, {loading: loadingUploadGenericDocument}] = useMutation<
    UploadGenericDocument,
    UploadGenericDocumentVariables
  >(uploadGenericDocumentMutation, {
    refetchQueries: [
      {
        query: getGenericDocumentQuery,
      },
    ],
    awaitRefetchQueries: true,
  });

  const [onUploadDocument, {loading: isUploadingDocument, called: isDocumentUploaded}] =
    useMutation<UploadDocumentToReportPack, UploadDocumentToReportPackVariables>(
      uploadDocumentToReportPack
    );
  const {
    data: dataReportPackQuery,
    error: errorReportPackQuery,
    loading: loadingReportPackQuery,
  } = useQuery<ReportPackQuery, ReportPackQueryVariables>(reportPackQuery, {
    variables: {
      reviewId,
    },
    skip: !isValidReviewId(reviewId),
  });

  const [
    onMarkReportPackAsNoLongerValid,
    {loading: loadingMarkReportPackAsNoLongerValid},
  ] = useMutation<MarkReportPackAsNoLongerValid, MarkReportPackAsNoLongerValidVariables>(
    markReportPackAsNoLongerValidMutation,
    {refetchQueries}
  );

  const [disableDocument] = useMutation(DISABLE_GENERIC_DOCUMENT_MUTATION);
  const [getDocumentUrl] = useLazyQuery(GET_GENERIC_DOCUMENT_URL_QUERY);

  return {
    onUpsertISATransferForm,
    onUpsertJISATransferForm,
    onUpsertIsaApplicationForm,
    onUpsertNomineeTransferForm,
    onUpsertJisaApplicationForm,
    onUpsertSippContributionForm,
    onDeleteForm,
    onUpsert,
    upsertLoading,
    upsertData,
    upsertError,
    onSubmit,
    submitLoading,
    onCancelReportPack,
    cancelLoading,
    onDeleteReportPack,
    deleteLoading,
    onUpsertMultreesDdmForm,
    onUpsertSippTransferForm,
    onUpsertSippDdmForm,
    genericDocuments: getGenericDocumentQueryData?.getGenericDocuments ?? [],
    loadingGenericDocuments,
    onUploadDocument,
    isUploadingDocument,
    isDocumentUploaded,
    onUploadGenericDocument,
    loadingUploadGenericDocument,
    onUpsertExpressionOfWishesForm,
    onUpsertFurtherActionRequiredForm,
    onUpsertBankDetailCollectionForm,
    amendLoading,
    onAmendReportPack,
    onResendReportPackEmail,
    resendEmailLoading,
    resendEmailData: resendEmailData?.resendReportPackEmail,
    dataReportPackQuery: dataReportPackQuery?.getReportPackByReview,
    loadingReportPackQuery,
    errorReportPackQuery,
    onMarkReportPackAsNoLongerValid,
    loadingMarkReportPackAsNoLongerValid,
    disableDocument,
    getDocumentUrl,
    refetchGenericDocuments,
    onUploadSmartflowToReportPack,
    uploadSmartflowData,
    uploadSmartflowError,
    loadingUploadSmartflow,
    onDeleteReportPackSmartflowExecution,
    loadingDeleteSmartflowExecution,
  };
}
